import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { API_URL } from "@/lib/env";
import { ApiError, apiUrl, parseErrorDetail } from "@/lib/api";
import { getAccessToken } from "@/lib/auth";
import { carmenAuth } from "@/lib/carmenAuth";
import { getImpersonation } from "@/lib/impersonation";

async function authedFetch(
  path: string,
  init: RequestInit = {},
  extraHeaders: Record<string, string> = {},
  accessToken?: string,
): Promise<Response> {
  // An explicit token (e.g. the one verifyOtp()/exchangeToken() just
  // returned during a recovery flow) takes precedence: it removes the
  // dependency on the shared client's getSession(), which can return null
  // at click-time in some browsers (notably email in-app webviews) even
  // right after a successful sign-in — leaving the request unauthenticated.
  // The implicit fallback goes through getAccessToken() (lib/auth.ts) —
  // e.g. SecurityTab's "add another passkey" flow, which has no explicit
  // token to hand in.
  let token = accessToken;
  if (!token) {
    token = (await getAccessToken()) ?? undefined;
  }
  const impersonation = getImpersonation();
  return fetch(apiUrl(path), {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(impersonation ? { "x-impersonate-user": impersonation.user_id } : {}),
      ...extraHeaders,
      ...(init.headers ?? {}),
    },
  });
}

export interface RegisterPasskeyOptions {
  /** Recovery-code session token (recovery-code flow). */
  recoverySession?: string;
  /**
   * Explicit access token to authenticate the registration with,
   * bypassing getSession(). Pass the token exchangeToken() returns in
   * recovery / magic-link flows so registration never depends on the shared
   * client's session being readable at click-time.
   */
  accessToken?: string;
  /**
   * Reset flow: the server omits excludeCredentials so re-enrollment works
   * on a device that still holds the old passkey (deletion happens at
   * reset-finalize, after the new passkey exists).
   */
  reset?: boolean;
}

export async function registerPasskey(
  deviceName: string,
  options: RegisterPasskeyOptions = {},
): Promise<{ credential_id: string }> {
  const { recoverySession, accessToken, reset } = options;
  const extra: Record<string, string> = recoverySession
    ? { "x-recovery-session": recoverySession }
    : {};
  const optsResp = await authedFetch(
    "/api/passkey/register/options",
    { method: "POST", ...(reset ? { body: JSON.stringify({ reset: true }) } : {}) },
    extra,
    accessToken,
  );
  if (!optsResp.ok) throw new ApiError(optsResp.status, await parseErrorDetail(optsResp));
  const opts = await optsResp.json();

  const credential = await startRegistration({ optionsJSON: opts });

  const verifyResp = await authedFetch(
    "/api/passkey/register/verify",
    { method: "POST", body: JSON.stringify({ credential, device_name: deviceName }) },
    extra,
    accessToken,
  );
  if (!verifyResp.ok) throw new ApiError(verifyResp.status, await parseErrorDetail(verifyResp));
  return verifyResp.json();
}

export async function finalizeReset(credentialId: string, accessToken?: string): Promise<void> {
  const resp = await authedFetch(
    "/api/passkey/reset-finalize",
    { method: "POST", body: JSON.stringify({ credential_id: credentialId }) },
    {},
    accessToken,
  );
  if (!resp.ok) throw new ApiError(resp.status, await parseErrorDetail(resp));
}

/** options → WebAuthn assertion → verify; returns the verify response. */
async function passkeyAssertion(
  apiBase: string,
  optionsBody: Record<string, string>,
  verifyExtra: Record<string, unknown>,
  verifyCredentials?: RequestCredentials,
): Promise<Response> {
  const optsResp = await fetch(apiUrl("/api/passkey/auth/options", apiBase), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(optionsBody),
  });
  if (!optsResp.ok) throw new ApiError(optsResp.status, await parseErrorDetail(optsResp));
  const opts = await optsResp.json();

  const assertion = await startAuthentication({ optionsJSON: opts });

  const verifyResp = await fetch(apiUrl("/api/passkey/auth/verify", apiBase), {
    method: "POST",
    headers: { "content-type": "application/json" },
    ...(verifyCredentials ? { credentials: verifyCredentials } : {}),
    body: JSON.stringify({
      credential: { ...assertion },
      session_id: opts.session_id,
      ...verifyExtra,
    }),
  });
  if (!verifyResp.ok) throw new ApiError(verifyResp.status, await parseErrorDetail(verifyResp));
  return verifyResp;
}

export async function authenticatePasskey(
  email?: string,
  apiBase: string = API_URL,
  surface?: "web" | "widget" | "mobile",
): Promise<{ user_id: string; email: string; magic_link_token: string }> {
  const resp = await passkeyAssertion(apiBase, email ? { email } : {}, {
    email,
    ...(surface ? { surface } : {}),
  });
  return resp.json();
}

/**
 * `legacy`: a pre-Phase-1 pod answered verify during a rolling deploy. It
 * ignored `handoff_session_id` and returned the usual token answer, so the
 * session is still unclaimed. Whether that token may be relayed to
 * /complete is the caller's decision (only ever for a pairing session).
 */
export type HandoffVerifyResult =
  | { handoff: "completed" }
  | { handoff: "pairing"; pairing_code: string }
  | { handoff: "legacy"; email: string; magic_link_token: string };

function parseHandoffVerify(body: unknown): HandoffVerifyResult | null {
  if (typeof body !== "object" || body === null) return null;
  if (!("handoff" in body)) {
    if (
      "email" in body &&
      typeof body.email === "string" &&
      "magic_link_token" in body &&
      typeof body.magic_link_token === "string"
    ) {
      return { handoff: "legacy", email: body.email, magic_link_token: body.magic_link_token };
    }
    return null;
  }
  if (body.handoff === "completed") return { handoff: "completed" };
  if (body.handoff === "pairing" && "pairing_code" in body && typeof body.pairing_code === "string") {
    return { handoff: "pairing", pairing_code: body.pairing_code };
  }
  return null;
}

/**
 * Widget handoff completed by the server inside the verify call (audit H1).
 * Windows: the widget put an HttpOnly verifier cookie for the API host into
 * its own sign-in window; `credentials: "include"` lets it travel with the
 * verify call, where the server checks it before anything is issued. Pairing
 * sessions: the server returns only the code. A Phase 1+ backend never
 * returns a token here; only a pre-Phase-1 pod does (`legacy`).
 */
export async function authenticatePasskeyForHandoff(
  apiBase: string,
  handoffSessionId: string,
): Promise<HandoffVerifyResult> {
  const resp = await passkeyAssertion(
    apiBase,
    {},
    { surface: "widget", handoff_session_id: handoffSessionId },
    "include",
  );
  const result = parseHandoffVerify(await resp.json());
  if (!result) throw new ApiError(resp.status, "Unexpected handoff response");
  return result;
}

export async function completeLogin(magicToken: string): Promise<void> {
  const { error } = await carmenAuth.exchangeToken({ token_hash: magicToken });
  if (error) throw error;
}
